/**
 * Keeps Box sync exclusion rules independent of presentation so every editor
 * applies the same backend-compatible limits before replacing an exclusion list.
 * Failures return stable discriminants for localized copy. Pathname entries are
 * globs, so their length limit applies to the regex source Bender will store.
 */
import { globToRegExpSource } from 'src/utils/globPattern';
import {
  hasGlobWildcard,
  type PathnameMatchMode,
  PathnameMatchModes,
  toPathnameGlob,
} from 'src/utils/pathnameMatchMode';

export const MAX_EXCLUDED_ENTRIES = 100;
export const MAX_PATHNAME_PATTERN_LENGTH = 512;

export const ExclusionValidationMessages = {
  InvalidFolderId: 'invalid-folder-id',
  EmptyPathname: 'empty-pathname',
  PathnameTooLong: 'pathname-too-long',
  WildcardNeedsAdvanced: 'wildcard-needs-advanced',
  Duplicate: 'duplicate',
  ListFull: 'list-full',
} as const;

export type ExclusionValidationMessage =
  typeof ExclusionValidationMessages[keyof typeof ExclusionValidationMessages];

const BOX_FOLDER_ID_PATTERN = /^\d{1,20}$/;

export const validateFolderIdEntry = (
  value: string,
  existing: readonly string[],
): ExclusionValidationMessage | null => {
  if (existing.length >= MAX_EXCLUDED_ENTRIES) {
    return ExclusionValidationMessages.ListFull;
  }
  if (!BOX_FOLDER_ID_PATTERN.test(value)) {
    return ExclusionValidationMessages.InvalidFolderId;
  }
  if (existing.includes(value)) {
    return ExclusionValidationMessages.Duplicate;
  }

  return null;
};

export const validatePathnameEntry = (
  value: string,
  existing: readonly string[],
): ExclusionValidationMessage | null => {
  if (existing.length >= MAX_EXCLUDED_ENTRIES) {
    return ExclusionValidationMessages.ListFull;
  }
  // A whitespace-only glob would only ever exclude names made entirely of spaces.
  if (value.trim().length === 0) {
    return ExclusionValidationMessages.EmptyPathname;
  }
  if (globToRegExpSource(value).length > MAX_PATHNAME_PATTERN_LENGTH) {
    return ExclusionValidationMessages.PathnameTooLong;
  }
  if (existing.includes(value)) {
    return ExclusionValidationMessages.Duplicate;
  }

  return null;
};

/** Validates what the user typed for a match mode, then the glob that mode assembles from it. */
export const validatePathnameInput = (
  mode: PathnameMatchMode,
  text: string,
  existing: readonly string[],
): ExclusionValidationMessage | null => {
  if (existing.length >= MAX_EXCLUDED_ENTRIES) {
    return ExclusionValidationMessages.ListFull;
  }
  if (text.trim().length === 0) {
    return ExclusionValidationMessages.EmptyPathname;
  }
  if (mode !== PathnameMatchModes.Advanced && hasGlobWildcard(text)) {
    return ExclusionValidationMessages.WildcardNeedsAdvanced;
  }

  return validatePathnameEntry(toPathnameGlob(mode, text), existing);
};
